import { useEffect, useState, useCallback } from 'react';
import API_BASE_URL from 'src/constants';
import { components, paths } from 'src/generated/v1';
import { $api } from './api';
import { handleUnauthorized } from './authToken';

export type Partners = components['schemas']['Page_Partner_'];
export type Partner = components['schemas']['Partner'];

export const getPartnerBySearchQuery = async (search: string) => {
  const accessToken = localStorage.getItem('access_token');
  if (!search) return undefined;
  try {
    const url = `${API_BASE_URL}portal/partners/search/${search}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized(accessToken);
      }
      throw new Error(`Failed to fetch partner data: ${response.statusText}`);
    }

    const data: Partner[] = await response.json();
    return data;
  } catch (err) {
    return undefined;
  }
};

export const useGetAllPartners = (params?: paths['/partners']['get']['parameters']['query']) => {
  return $api.useQuery('get', '/partners', {
    params: {
      query: params,
    },
  });
};

export const updatePartnerById = async (id: number, data: any): Promise<Partner | undefined> => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const { chat, ...partnerData } = data;

    const response = await fetch(`${API_BASE_URL}portal/partners/${id}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(partnerData),
    });

    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized(accessToken);
      }
      throw new Error(`Failed to update partner data: ${response.statusText}`);
    }

    const res: Partner = await response.json();
    return res;
  } catch (err) {
    return undefined;
  }
};

export const usePartners = () => {
  const accessToken = localStorage.getItem('access_token');
  const [partners, setPartners] = useState<Partners>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(100);
  const [filterId, setFilterId] = useState<string | null>(null);
  const [domain, setDomain] = useState<string | null>(null);

  const getPartners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_BASE_URL}portal/partners?page=${page}&size=${size}`;
      if (filterId && domain) {
        url = `${url}&filter_id=${filterId}&domain=${domain}`;
      }
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(accessToken);
        }
        throw new Error(`Failed to fetch partner data: ${response.statusText}`);
      }

      const data: Partners = await response.json();
      setPartners(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch partner data');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    getPartners();
  }, [filterId, domain, page, size]);

  useEffect(() => {
    getPartners();
  }, [getPartners]);

  return { partners, loading, error, getPartners, setPage, setSize, setFilterId, setDomain };
};

const usePartner = (id?: number) => {
  const accessToken = localStorage.getItem('access_token');
  const [partner, setPartner] = useState<Partner | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getPartner = useCallback(
    async (id: number) => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}portal/partners/${id}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleUnauthorized(accessToken);
          }
          throw new Error(`Failed to fetch partner data: ${response.statusText}`);
        }

        const data: Partner = await response.json();
        setPartner(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch partner data');
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  const updatePartner = useCallback(
    async (partnerData: Partner) => {
      if (!partner?.id) return;
      setLoading(true);
      setError(null);
      try {
        const dataObject = {
          address: partnerData.address,
          email: partnerData.email,
          name: partnerData.name,
          phone: partnerData.phone,
        };

        const res = await updatePartnerById(partner.id, dataObject);
        if (res) {
          setPartner(partnerData);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update partner data');
      } finally {
        setLoading(false);
      }
    },
    [accessToken, partner?.id],
  );

  useEffect(() => {
    if (id) {
      getPartner(id);
    }
  }, [id, getPartner]);

  return { partner, loading, error, getPartner, updatePartner };
};

export default usePartner;
